import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fileDownload from "js-file-download";
import toast from "react-hot-toast";
import { ADD_ISSUES_TO_RELEASE, CREATE_RELEASE, RELEASES } from "services/endPoints";
import { privateRequest } from "services/privateRequest";
import { getProjectId } from "utils/utils";

const page = 10;

// 🔹 Utility to normalize API response
const normalizeResponse = (payload) => {
    if (!payload) return null;
    return payload?.data || payload;
};

// 🔹 1. Get releases by projectId
export const getReleases = createAsyncThunk(
    "release/fetchReleases",
    async (
        { search = "", status = "RELEASED", pageNo = 1, pageSize = page },
        { rejectWithValue }
    ) => {
        try {
            const res = await privateRequest.get(RELEASES, {
                params: { search, status, pageNo, pageSize },
                headers: { "Project-id": getProjectId() },
            });
            return res.data;
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to fetch releases");
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getReleasesAll = createAsyncThunk(
    "releaseAll/fetchReleases",
    async (
        { search = "", status = "", pageNo = 1, pageSize = page, projectId },
        { rejectWithValue }
    ) => {
        console.log(projectId)
        try {
            const res = await privateRequest.get(RELEASES, {
                params: { search, status, pageNo, pageSize },
                headers: { "project-id": projectId},
            });
            console.log(res?.data?.data?.data);

            return res?.data?.data?.data;
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to fetch releases");
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// 🔹 2. Get single release by ID
export const getReleaseById = createAsyncThunk(
    "release/fetchReleaseById",
    async (releaseId, { rejectWithValue }) => {
        try {
            const res = await privateRequest.get(`${RELEASES}/${releaseId}`);
            return res.data;
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to fetch release");
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// 🔹 3. Create release
export const createRelease = createAsyncThunk(
    "release/createRelease",
    async (payload, { dispatch, rejectWithValue }) => {
        try {
            const res = await privateRequest.post(CREATE_RELEASE, payload, {
                headers: { "Project-id": getProjectId() },
            });

            if (res?.data?.statusCode === 201 || res?.data?.statusCode === 200) {
                toast.success(res?.data?.message || "Version created successfully");
                await dispatch(getReleases({ search: "", status: "", pageNo: 1, pageSize: page }));
                return res.data; // just return success message
            }
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Thunk for adding issues
export const addIssuesToRelease = createAsyncThunk(
    "release/addIssuesToRelease",
    async ({ releaseId, issueIds }, { dispatch, rejectWithValue }) => {
        try {
            const res = await privateRequest.post(
                ADD_ISSUES_TO_RELEASE,
                { releaseId, issueIds },
                { headers: { "Project-id": getProjectId() } }
            );
            toast.success(res?.data?.message || "Issues added successfully");
            await dispatch(getReleaseIssues({
                releaseId,
                search: "",
                status: "",
                assigneeIds: "",
                epicIds: "",
                pageNo: 1,
                pageSize: 10,
            }));
            await dispatch(getReleases({ search: "", status: "", pageNo: 1, pageSize: page }));
            return res.data;
        } catch (error) {
            // toast.error(error?.response?.data?.message || "Failed to add issues");
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// 🔹 4. Update release
export const updateRelease = createAsyncThunk(
    "release/updateRelease",
    async ({ id, payload }, { dispatch, rejectWithValue }) => {
        try {
            const res = await privateRequest.put(`${RELEASES}/${id}`, payload, {
                headers: { "Project-id": getProjectId() }
            });
            toast.success(res?.data?.message || "Release updated successfully");
            await dispatch(getReleases({ search: "", status: "", pageNo: 1, pageSize: page }));
            return {
                apiResponse: res.data,
                updatedData: { ...payload, id: id }
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// 🔹 5. Update release status
export const updateReleaseStatus = createAsyncThunk(
    "release/updateReleaseStatus",
    async ({ releaseId, payload }, { dispatch, rejectWithValue }) => {
        try {
            const res = await privateRequest.put(
                `${RELEASES}/${releaseId}/status`,
                payload,
                { headers: { "Project-id": getProjectId() } }
            );

            toast.success(res?.data?.message || "Status updated successfully");

            // refresh releases after update
            await dispatch(getReleases({ search: "", status: "", pageNo: 1, pageSize: page }));

            return res.data;
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to update status");
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// 🔹 6. Get issues inside a release
// export const getReleaseIssues = createAsyncThunk(
//     "release/fetchReleaseIssues",
//     async (releaseId, { rejectWithValue }) => {
//         try {
//             const res = await privateRequest.get(`${RELEASES}/${releaseId}/issues`);
//             return res.data;
//         } catch (error) {
//             toast.error(error?.response?.data?.message || "Failed to fetch issues");
//             return rejectWithValue(error.response?.data || error.message);
//         }
//     }
// );

export const downloadReleaseNote = createAsyncThunk(
    "release/downloadReleaseNote",
    async (releaseId, { rejectWithValue }) => {
        try {
            const response = await privateRequest.get(`api/release/generate-pdf/${releaseId}`, {
                responseType: "blob",
            });

            const contentDisposition = response.headers["content-disposition"];
            let fileName = "ReleaseNote.pdf";

            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match?.[1]) fileName = decodeURIComponent(match[1]);
            }

            fileDownload(response.data, fileName);
            toast.success("Release note downloaded successfully");
            return true;
        } catch (error) {
            console.error("Error downloading release note:", error);
            toast.error("Failed to download release note");
            return rejectWithValue(error.response?.data || "Failed to download release note");
        }
    }
);

export const getReleaseIssues = createAsyncThunk(
    "release/fetchReleaseIssues",
    async (
        {
            releaseId,
            search = "",
            status = "",
            assigneeIds = "",
            epicIds = "",
            pageNo = 1,
            pageSize = 5,
        },
        { rejectWithValue }
    ) => {
        try {
            const res = await privateRequest.get(`${RELEASES}/${releaseId}/issues`, {
                params: {
                    search,
                    status,
                    assigneeIds,
                    epicIds,
                    pageNo,
                    pageSize,
                },
            });

            return res.data;
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                "Failed to fetch release issues"
            );
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// 🔹 7. Delete release
export const deleteRelease = createAsyncThunk(
    "release/deleteRelease",
    async (releaseId, { rejectWithValue }) => {
        try {
            const res = await privateRequest.delete(`${RELEASES}/${releaseId}`);
            toast.success(res?.data?.message || "Release deleted successfully");
            return releaseId;
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to delete release");
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);


// DELETE a release issue
export const deleteReleaseIssue = createAsyncThunk(
    "release/deleteReleaseIssue",
    async ({ releaseId, releaseIssueId }, { rejectWithValue }) => {
        try {
            const response = await privateRequest.delete(
                `${RELEASES}/${releaseId}/issues/${releaseIssueId}`,
                {
                    headers: { "Project-id": getProjectId() } // Add missing header
                }
            );
            toast.success("Issue removed from release successfully");
            return { releaseIssueId, releaseId }; // Return both IDs for better state management
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to remove issue from release");
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const moveReleaseIssue = createAsyncThunk(
    'release/moveReleaseIssue',
    async ({ issueId, sourceReleaseId, destinationReleaseId, note }, { rejectWithValue }) => {
        try {
            const response = await privateRequest.put(`api/release/issues/${issueId}/move`, { sourceReleaseId, destinationReleaseId, note });
            toast.success("Issue moved successfully");
            return response.data;
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to move issue");
            return rejectWithValue(error.response?.data || "Something went wrong");
        }
    }
);

const releaseSlice = createSlice({
    name: "release",
    initialState: {
        releases: [],
        releasesAll:[],
        releaseDetails: null,
        releaseIssues: [],
        loading: false,
        error: null,
        downloading: false,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Get all releases
            .addCase(getReleases.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getReleases.fulfilled, (state, action) => {
                state.loading = false;
                state.releases = action.payload?.data?.data || [];
                state.totalElements = action.payload?.data?.count?.total || 0;
            })
            .addCase(getReleases.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

               .addCase(getReleasesAll.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getReleasesAll.fulfilled, (state, action) => {
                state.loading = false;
                state.releasesAll = action.payload?.data?.data || [];
                state.totalElements = action.payload?.data?.count?.total || 0;
            })
            .addCase(getReleasesAll.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Create release
            .addCase(createRelease.fulfilled, (state, action) => {
                // let obj = action.payload;
                // obj.releaseStatus = "UNRELEASED";
                // const release = normalizeResponse(obj);
                // if (!release) return;
                // if (Array.isArray(state.releases)) {
                //     state.releases.push(release);
                // } else {
                //     state.releases = [release];
                // }
            })
            .addCase(createRelease.rejected, (state, action) => {
                state.error = action.payload;
            })

            // Update release
            .addCase(updateRelease.fulfilled, (state, action) => {
                // const { updatedData } = action.payload;
                // if (!updatedData) return;

                // const idx = state.releases.findIndex((r) => r.id === updatedData.id);
                // if (idx >= 0) {
                //     state.releases[idx] = { ...state.releases[idx], ...updatedData };
                // }
            })
            .addCase(updateRelease.rejected, (state, action) => {
                state.error = action.payload;
            })

            // Update status
            .addCase(updateReleaseStatus.fulfilled, (state, action) => {
                // const { releaseId, status } = action.meta.arg; // the dispatched payload
                // const idx = state.releases.findIndex(r => r.id === releaseId);
                // if (idx >= 0) {
                //     state.releases[idx] = {
                //         ...state.releases[idx],
                //         releaseStatus: status, // update immediately in table
                //     };
                // }
            })
            .addCase(updateReleaseStatus.rejected, (state, action) => {
                state.error = action.payload;
            })

            // Delete release
            .addCase(deleteRelease.fulfilled, (state, action) => {
                state.releases = state.releases.filter((r) => r.id !== action.payload);
            })
            .addCase(deleteRelease.rejected, (state, action) => {
                state.error = action.payload;
            })

            // Get release details
            .addCase(getReleaseById.fulfilled, (state, action) => {
                state.releaseDetails = normalizeResponse(action.payload);
            })
            .addCase(getReleaseById.rejected, (state, action) => {
                state.error = action.payload;
            })

            // Get release issues
            .addCase(getReleaseIssues.fulfilled, (state, action) => {
                state.loading = false;
                state.releaseIssues = action.payload?.data?.data || [];
                state.totalReleaseIssues = action.payload?.data?.count?.total || 0;
            })
            .addCase(getReleaseIssues.rejected, (state, action) => {
                state.error = action.payload;
            })
            // In your releaseSlice, update the deleteReleaseIssue.fulfilled case
            .addCase(deleteReleaseIssue.fulfilled, (state, action) => {
                const { releaseIssueId } = action.payload;
                state.releaseIssues = state.releaseIssues.filter(
                    (issue) => issue.issueId !== releaseIssueId
                );
                state.totalReleaseIssues = Math.max(0, state.totalReleaseIssues - 1);
            })
            .addCase(downloadReleaseNote.pending, (state) => {
                state.downloadLoading = true;
            })
            .addCase(downloadReleaseNote.fulfilled, (state) => {
                state.downloadLoading = false;
            })
            .addCase(downloadReleaseNote.rejected, (state) => {
                state.downloadLoading = false;
            });
    },
});

export default releaseSlice.reducer;
